import { BoundaryPoint } from "../../types";

// Helper function to calculate distance from point to line segment
const distanceToLineSegment = (
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number => {
  const A = px - x1;
  const B = py - y1;
  const C = x2 - x1;
  const D = y2 - y1;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;

  if (lenSq !== 0) param = dot / lenSq;

  let xx, yy;

  if (param < 0) {
    xx = x1;
    yy = y1;
  } else if (param > 1) {
    xx = x2;
    yy = y2;
  } else {
    xx = x1 + param * C;
    yy = y1 + param * D;
  }

  const dx = px - xx;
  const dy = py - yy;

  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Find the closest line segment to a point
 */
export const findClosestLineSegment = (
  lng: number,
  lat: number,
  boundaryPoints: BoundaryPoint[]
): { index: number; distance: number } => {
  let closestSegmentIndex = -1;
  let minDistance = Infinity;

  for (let i = 0; i < boundaryPoints.length - 1; i++) {
    const p1 = boundaryPoints[i];
    const p2 = boundaryPoints[i + 1];

    const distance = distanceToLineSegment(
      lng,
      lat,
      p1.coordinates.longitude,
      p1.coordinates.latitude,
      p2.coordinates.longitude,
      p2.coordinates.latitude
    );

    if (distance < minDistance) {
      minDistance = distance;
      closestSegmentIndex = i;
    }
  }

  return { index: closestSegmentIndex, distance: minDistance };
};

/**
 * Insert a new boundary point at a specific position
 */
export const insertBoundaryPoint = (
  boundaryPoints: BoundaryPoint[],
  newPoint: BoundaryPoint,
  insertIndex: number
): BoundaryPoint[] => {
  const updatedBoundaryPoints = [...boundaryPoints];
  updatedBoundaryPoints.splice(insertIndex, 0, newPoint);

  // Update order for all points after the insertion
  for (let i = insertIndex + 1; i < updatedBoundaryPoints.length; i++) {
    updatedBoundaryPoints[i].order = i;
  }

  return updatedBoundaryPoints;
};

/**
 * Create a new boundary point
 */
export const createBoundaryPoint = (
  lng: number,
  lat: number,
  order: number
): BoundaryPoint => ({
  id: `boundary-${Date.now()}-${order}`,
  coordinates: { longitude: lng, latitude: lat },
  order,
});
